import { Injectable, inject } from '@angular/core';
import { Auth, authState, user } from '@angular/fire/auth';
import { Firestore, collection, doc, getDoc } from '@angular/fire/firestore';
import { firstValueFrom, take } from 'rxjs';

/**
 * Firebase users are shared with other applications in the same project, so being
 * authenticated is not enough to use this webapp — the account must belong to at
 * least one team. Membership lives in `userTeams/{uid}.teams`.
 *
 * The answer is cached per uid so the guard does not hit Firestore on every route
 * activation. The cache expires after CACHE_TTL_MS so membership granted or revoked
 * while a tab stays open takes effect without a reload, and it is dropped outright
 * whenever the signed-in user changes (sign out, but also impersonate/unimpersonate,
 * which swap the current user).
 */
@Injectable({ providedIn: 'root' })
export class TeamMembershipService {

    private static readonly CACHE_TTL_MS = 5 * 60 * 1000;

    private cache = new Map<string, { hasTeam: boolean, expires: number }>();
    private lastUid: string | null = null;

    constructor(private firestore: Firestore = inject(Firestore), private afAuth: Auth = inject(Auth)) {
        authState(this.afAuth).subscribe(user => {
            const uid = user ? user.uid : null;
            if (uid !== this.lastUid) {
                this.lastUid = uid;
                this.clear();
            }
        });
    }

    async hasTeam(): Promise<boolean> {
        // Wait for Firebase to restore the persisted session — the same signal
        // AngularFire's AuthGuard waits on. Router guards in a canActivate array run
        // concurrently, so reading `currentUser` synchronously would see null on a
        // hard reload and bounce a legitimate member to /no-access.
        const currentUser = await firstValueFrom(user(this.afAuth).pipe(take(1)));
        if (!currentUser) {
            return false;
        }
        const cached = this.cache.get(currentUser.uid);
        if (cached && cached.expires > Date.now()) {
            return cached.hasTeam;
        }

        let hasTeam = false;
        try {
            const snapshot = await getDoc(doc(collection(this.firestore, 'userTeams'), currentUser.uid));
            const teams = snapshot.exists() ? snapshot.data().teams : null;
            hasTeam = Array.isArray(teams) && teams.length > 0;
        }
        catch (err) {
            // Fail closed: a rules denial or network error must not grant access.
            console.warn(err);
            return false;
        }

        this.cache.set(currentUser.uid, { hasTeam: hasTeam, expires: Date.now() + TeamMembershipService.CACHE_TTL_MS });
        return hasTeam;
    }

    clear(uid?: string) {
        if (uid) {
            this.cache.delete(uid);
        }
        else {
            this.cache.clear();
        }
    }

}
